<template>
  <div class="daily-aggregation-step">
    <!-- カテゴリ見出し + 「完了する」ボタン（同一ライン） -->
    <div class="category-bar d-flex align-center mb-4">
      <h2 class="category-heading">
        {{ categoryLabel }}
      </h2>

      <v-spacer />

      <!-- 完了する: カテゴリ配下の「未完了」集計表を依存順に「リセット→再集計→保存」する
           （完了済みはスキップ）。全て保存済みのときは無効化し、理由をツールチップで案内。 -->
      <v-tooltip
        v-if="!isLoading && aggregationsData.length > 0"
        bottom
        :disabled="!allSaved"
      >
        <template #activator="{ on, attrs }">
          <div
            class="d-inline-block"
            v-bind="attrs"
            v-on="on"
          >
            <v-btn
              color="primary"
              :ripple="false"
              :loading="isBulkCompleting"
              :disabled="!canComplete"
              @click="onComplete"
            >
              更新
            </v-btn>
          </div>
        </template>
        <span>すべて保存済みです</span>
      </v-tooltip>
    </div>

    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <template v-else>
      <!-- 集計表が無い場合 -->
      <v-alert
        v-if="aggregationsData.length === 0"
        type="info"
        text
        dense
      >
        {{ categoryLabel }}の集計表が設定されていません
      </v-alert>

      <template v-else>
        <!-- 未保存サマリー -->
        <v-alert
          v-if="unsavedCount > 0"
          color="warning"
          text
          class="unsaved-summary mb-4"
        >
          <div class="d-flex align-center">
            <v-icon
              color="warning"
              class="mr-3"
            >
              {{ icons.mdiAlert }}
            </v-icon>
            <span class="font-weight-bold warning--text">
              {{ categoryLabel }}に未保存が {{ unsavedCount }} 件あります
            </span>
          </div>
        </v-alert>

        <!-- 要再集計サマリー（集計後に元データや依存先が変更された場合）。
             保存済みの結果が最新の値を反映していないため、「完了する」で
             リセット→再集計→保存をやり直す必要がある。 -->
        <v-alert
          v-if="needsReaggregationCount > 0"
          color="warning"
          text
          class="needs-reaggregation-summary mb-4"
        >
          <div class="d-flex align-center">
            <v-icon
              color="warning"
              class="mr-3"
            >
              {{ icons.mdiSyncAlert }}
            </v-icon>
            <span class="font-weight-bold warning--text">
              保存後にタイムカード・伝票や依存先の集計が変更されています。保存済みの結果が古くなっているため、更新が必要です
            </span>
          </div>
        </v-alert>

        <!-- 完了する押下後の進捗チェックリスト（型ごとに集計表の順次実行を表示） -->
        <v-card
          v-if="isBulkCompleting"
          outlined
          class="bulk-progress-card py-8 px-6 text-center"
        >
          <div class="bulk-progress-icon">
            ⏳
          </div>
          <h3 class="bulk-progress-title mt-4 mb-1">
            集計を計算しています…
          </h3>
          <p class="bulk-progress-summary text--secondary mb-4">
            {{ bulkDoneCount }} / {{ bulkTotalCount }} 完了
          </p>
          <div class="bulk-progress-list mx-auto">
            <div
              v-for="(item, idx) in progressItems"
              :key="item.id"
              class="bulk-progress-row"
              :class="`bulk-progress-row--${item.state}`"
            >
              <span class="bulk-progress-icon-cell">
                <v-icon
                  v-if="item.state === 'succeeded'"
                  color="success"
                  size="18"
                >{{ icons.mdiCheck }}</v-icon>
                <v-icon
                  v-else-if="item.state === 'failed'"
                  color="error"
                  size="18"
                >{{ icons.mdiAlertCircle }}</v-icon>
                <v-icon
                  v-else-if="item.state === 'skipped'"
                  color="grey"
                  size="18"
                >{{ icons.mdiSkipNext }}</v-icon>
                <v-progress-circular
                  v-else-if="item.state === 'running'"
                  indeterminate
                  size="16"
                  width="2"
                  color="primary"
                />
                <span
                  v-else
                  class="bulk-progress-step-number"
                >{{ idx + 1 }}</span>
              </span>
              <span class="bulk-progress-label">
                {{ item.name }}<span v-if="item.state === 'running'">…</span>
              </span>
            </div>
          </div>
        </v-card>

        <!-- スプレッドシート -->
        <v-card
          v-else
          outlined
          class="spread"
        >
          <!-- シートのツールバー -->
          <div class="sheet-bar d-flex align-center px-4 py-2">
            <span class="sheet-title font-weight-bold mr-3">{{ activeSheet.name }}</span>
            <sheet-status-chip
              v-if="activeStatus !== 'uncounted'"
              :status="activeStatus"
            />
            <v-spacer />
            <span
              v-if="activeTimestamp"
              class="sheet-meta text--disabled mr-3"
            >
              最終集計日時: {{ activeTimestamp }}
            </span>

            <!-- 個別集計: この集計表だけを「リセット→集計→保存」する -->
            <v-btn
              small
              depressed
              class="mr-1 individual-agg-btn"
              :loading="isActiveIndividualCompleting"
              :disabled="isBulkCompleting || isActiveIndividualCompleting"
              @click="onIndividualComplete"
            >
              個別集計
            </v-btn>

            <!-- ⋮ メニュー（保存・再集計・リセット再集計は「完了する」に統合済み。
                 残すのは適用ユーザー変更とクリップボードコピーのみ） -->
            <v-menu
              bottom
              left
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-subheader>操作</v-subheader>

                <!-- 完了済みも含めて全て集計（force-all）。完了済みも reset→集計→保存し直す。
                     カテゴリ（型）全体が対象。押下で警告モーダルを出してから実行。
                     無効化条件はハンドラ（onCompleteAll）の中断条件と揃える：シート単位の
                     集計中（isAnyJobInProgress）も含めないと「押せるのに無反応」になる。 -->
                <v-list-item
                  :disabled="isBulkCompleting || isActiveIndividualCompleting || isAnyJobInProgress"
                  @click="onCompleteAll"
                >
                  <v-list-item-title class="force-recompute-title">
                    保存済みも含めて全て更新
                  </v-list-item-title>
                </v-list-item>

                <!-- 適用ユーザーの変更（ユーザー集計のときのみ） -->
                <v-list-item
                  v-if="isUserAggregation"
                  @click="usersDialogOpen = true"
                >
                  <v-list-item-title>適用ユーザーの変更</v-list-item-title>
                </v-list-item>

                <!-- クリップボードにコピー -->
                <v-list-item @click="copyToClipboard">
                  <v-list-item-title>クリップボードにコピー</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- シート本体 -->
          <div class="sheet-body">
            <!-- エラー -->
            <div
              v-if="activeStatus === 'error'"
              class="pa-6"
            >
              <v-alert
                type="error"
                text
                dense
              >
                【集計に失敗しました】{{ activeErrorMessage || '原因不明のエラー' }}<br />
                再集計してもエラーが続く場合はサポートにお問い合わせください。
              </v-alert>
            </div>

            <!-- 集計中 -->
            <div
              v-else-if="activeStatus === 'progress'"
              class="pa-6"
            >
              <p class="text--secondary mb-2">
                集計を実行しています。しばらくお待ちください。
              </p>
              <v-progress-linear
                indeterminate
                color="primary"
                rounded
              />
            </div>

            <!-- スプレッドシート形式の罫線グリッド -->
            <!-- パディングは付けない（name 列の sticky 固定を左端ぴったりに -->
            <!-- 付けるため、また上下の余分な余白も出さないため）。 -->
            <div
              v-else-if="activeAggregation"
              class="grid-wrap"
            >
              <aggregation-grid
                :key="`sheet-grid-${activeSheet.id}`"
                ref="gridRef"
                :aggregation="activeAggregation"
                :loading="!!loadingStatuses[activeSheet.id]"
                @save="$emit('save-cell', $event)"
                @busy-change="isGridBusy = $event"
                @select-user="openUserPanel"
              />
            </div>

            <!-- 集計データ未取得（ジョブ未実行） -->
            <div
              v-else
              class="pa-6"
            >
              <v-alert
                type="error"
                :icon="icons.mdiAlertCircle"
                text
                dense
              >
                まだ集計が実行されていません。集計する場合は「更新」または「個別集計」を押してください。
              </v-alert>
            </div>
          </div>

          <!-- シートタブ（下部・Excel風） -->
          <div class="sheet-tabs d-flex">
            <button
              v-for="(sheet, idx) in aggregationsData"
              :key="`sheet-tab-${sheet.attributes.id}`"
              type="button"
              class="sheet-tab"
              :class="{ active: idx === activeIndex }"
              @click="activeIndex = idx"
            >
              <v-icon
                small
                :color="statusColor(sheet.attributes.id)"
                class="mr-1"
              >
                {{ statusIcon(sheet.attributes.id) }}
              </v-icon>
              {{ sheet.attributes.name }}
            </button>
          </div>
        </v-card>

        <!-- 適用ユーザーの変更ダイアログ -->
        <aggregation-users-dialog
          v-if="isUserAggregation"
          v-model="usersDialogOpen"
          :aggregation-users="activeAggregationUsers"
          :available-users="activeAvailableUsers"
          @fetch-available-users="onFetchAvailableUsers"
          @create="onCreateUser"
          @destroy="onDestroyUser"
        />

        <!-- 「完了済みも含めて全て集計」の警告モーダル。
             保存済みも含めて再集計し直すため、確認してから実行する。 -->
        <v-dialog
          v-model="forceAllDialogOpen"
          max-width="440"
        >
          <v-card>
            <v-card-title class="text-h6 error--text">
              確認
            </v-card-title>
            <v-card-text>
              現在保存されている結果はすべて破棄され、再集計後の値で上書きされます。
              よろしいですか？
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                @click="forceAllDialogOpen = false"
              >
                キャンセル
              </v-btn>
              <v-btn
                color="error"
                @click="confirmCompleteAll"
              >
                全て再集計
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </template>

    <!-- ユーザー名クリックで開く販売実績パネル（右ドロワー） -->
    <user-performance-panel
      v-model="isPanelOpen"
      :date="date"
      :user-name="selectedUser ? selectedUser.userName : ''"
      :items="selectedUserSummary.items"
      :total-count="selectedUserSummary.totalCount"
      :total-price="selectedUserSummary.totalPrice"
      :working-minutes="selectedUserSummary.workingMinutes"
      :is-loading="isPerformanceLoading"
    />
  </div>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiCheckBold,
  mdiDotsVertical,
  mdiAlert,
  mdiAlertCircle,
  mdiTimerSand,
  mdiCheckCircle,
  mdiClockOutline,
  mdiSyncAlert,
  mdiCheck,
  mdiSkipNext,
} from '@mdi/js'
import AggregationGrid from './AggregationGrid.vue'
import AggregationUsersDialog from './AggregationUsersDialog.vue'
import SheetStatusChip from './SheetStatusChip.vue'
import UserPerformancePanel from '@/views/v2-temp/eod-closing/components/UserPerformancePanel.vue'
import useUserPerformanceSummaries from '@/views/v2-temp/eod-closing/composables/useUserPerformanceSummaries'

const STATUS_ICON = {
  error: mdiAlertCircle,
  unsaved: mdiAlert,
  progress: mdiTimerSand,
  saved: mdiCheckCircle,
  uncounted: mdiClockOutline,
  needs_reaggregation: mdiSyncAlert,
}
const STATUS_COLOR = {
  error: 'error',
  unsaved: 'warning',
  progress: 'secondary',
  saved: 'success',
  // 未集計はステッパーの丸（赤）と揃えて赤にする（グレーだと「問題なし」に見えるため）
  uncounted: 'error',
  needs_reaggregation: 'warning',
}

export default {
  name: 'DailyAggregationStep',
  components: {
    AggregationGrid,
    AggregationUsersDialog,
    SheetStatusChip,
    UserPerformancePanel,
  },
  props: {
    // 営業日（販売実績パネルのデータ取得に使用）
    date: {
      type: String,
      default: '',
    },
    // 表示対象カテゴリの集計表データ（aggregationsData の該当タイプ分）
    aggregationsData: {
      type: Array,
      default: () => [],
    },
    // 集計表ID -> 集計表（結果込み）のマップ
    aggregationsMap: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> ジョブ状態
    jobStatuses: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> エラーメッセージ
    jobErrorMessages: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> { createdAt, updatedAt }
    jobTimestamps: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> ローディング状態
    loadingStatuses: {
      type: Object,
      default: () => ({}),
    },
    // 全体ローディング
    isLoading: {
      type: Boolean,
      default: false,
    },
    // カテゴリ表示名（ユーザー集計 / 卓集計 / 店舗集計）
    categoryLabel: {
      type: String,
      default: '',
    },
    // 集計表タイプ（EodClosingUserAggregation 等）。bulk_complete の起動に使う。
    type: {
      type: String,
      default: '',
    },
    // このカテゴリの bulk_complete 進捗。
    // { status: 'in_progress'|'completed', total, succeeded, failed, skipped, results } | null
    bulkStatus: {
      type: Object,
      default: null,
    },
    // シート状態判定関数（集計表ID -> 'error'|'unsaved'|'progress'|'saved'）
    statusResolver: {
      type: Function,
      default: () => 'saved',
    },
    // 集計表ID -> 適用候補ユーザー一覧（適用ユーザーの変更ダイアログ用）
    availableUsers: {
      type: Object,
      default: () => ({}),
    },
    // 集計表ID -> 個別集計（リセット→集計→保存）の実行中フラグ
    individualCompletingIds: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const activeIndex = ref(0)

    // 集計表の増減に追従。範囲外になったら先頭に戻す。
    watch(() => props.aggregationsData.length, len => {
      if (activeIndex.value >= len) activeIndex.value = 0
    })

    const activeSheet = computed(() => {
      const data = props.aggregationsData[activeIndex.value]

      return data ? { id: data.attributes.id, name: data.attributes.name } : { id: null, name: '' }
    })

    const activeStatus = computed(() => (
      activeSheet.value.id ? props.statusResolver(activeSheet.value.id) : 'saved'
    ))

    // グリッドが編集中・保存発行待ち（busy）かどうか。AggregationGrid から受け取る。
    const isGridBusy = ref(false)

    const activeAggregation = computed(() => props.aggregationsMap[activeSheet.value.id] || null)

    const activeErrorMessage = computed(() => props.jobErrorMessages[activeSheet.value.id] || '')

    const activeTimestamp = computed(() => {
      const ts = props.jobTimestamps[activeSheet.value.id]
      if (!ts || !ts.updatedAt) return ''

      try {
        const d = new Date(ts.updatedAt)

        return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} `
          + `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      } catch (e) {
        return ''
      }
    })

    const unsavedCount = computed(() => (
      props.aggregationsData.reduce((acc, data) => (
        props.statusResolver(data.attributes.id) === 'unsaved' ? acc + 1 : acc
      ), 0)
    ))

    const needsReaggregationCount = computed(() => (
      props.aggregationsData.reduce((acc, data) => (
        props.statusResolver(data.attributes.id) === 'needs_reaggregation' ? acc + 1 : acc
      ), 0)
    ))

    const statusIcon = id => STATUS_ICON[props.statusResolver(id)] || STATUS_ICON.saved
    const statusColor = id => STATUS_COLOR[props.statusResolver(id)] || STATUS_COLOR.saved

    // --- 適用ユーザーの変更 ----------------------------------------------
    // ユーザー集計（EodClosingUserAggregation）のときだけメニュー項目を出す。
    const activeAggregationType = computed(() => {
      const data = props.aggregationsData[activeIndex.value]

      return data?.attributes?.type || ''
    })
    const isUserAggregation = computed(() => (
      activeAggregationType.value === 'EodClosingUserAggregation'
    ))

    const usersDialogOpen = ref(false)

    // アクティブシートの適用済みユーザー（aggregationsData 側に aggregationUsers が載る）
    const activeAggregationUsers = computed(() => {
      const data = props.aggregationsData[activeIndex.value]

      return data?.attributes?.aggregationUsers?.data || []
    })

    // アクティブシートの適用候補ユーザー
    const activeAvailableUsers = computed(() => (
      props.availableUsers[activeSheet.value.id] || []
    ))

    const onFetchAvailableUsers = callback => {
      emit('fetch-available-users', { id: activeSheet.value.id, callback })
    }

    const onCreateUser = ({ userId, callback }) => {
      emit('create-user', { id: activeSheet.value.id, userId, callback })
    }

    const onDestroyUser = ({ id, userId, callback }) => {
      emit('destroy-user', {
        aggregationId: activeSheet.value.id, id, userId, callback,
      })
    }

    // --- 完了する（bulk_complete） --------------------------------------
    // カテゴリ配下の集計表を依存順に「リセット→再集計→保存」する一括処理。
    // 旧来の「保存」「再集計」「リセットして再集計」「日計ジョブ一括実行」を集約。

    // カテゴリ内に集計中（progress）の集計表があるか
    const isAnyJobInProgress = computed(() => (
      props.aggregationsData.some(data => (
        props.statusResolver(data.attributes.id) === 'progress'
      ))
    ))

    // bulk_complete 実行中か（親から渡される進捗で判定）
    const isBulkCompleting = computed(() => props.bulkStatus?.status === 'in_progress')

    // 進捗表示用（確定した件数 / 総数）
    const bulkTotalCount = computed(() => props.bulkStatus?.total ?? 0)
    const bulkDoneCount = computed(() => {
      const b = props.bulkStatus
      if (!b) return 0

      return (b.succeeded ?? 0) + (b.failed ?? 0) + (b.skipped ?? 0)
    })

    // 進捗チェックリスト表示用。実行対象の集計表ごとに 1 行、状態を導出する。
    // results に状態がある→確定（succeeded / failed / skipped）。無い→未確定（running）。
    // 部分実行（完了済みスキップ）では実行対象だけを列挙する。未対象（保存済み）を
    // 含めると永久に running 表示になるため、起動時の expectedOrder（= aggregationIds）で
    // 絞る。aggregationIds が空（force-all 等）なら全集計表を対象とする。
    const progressItems = computed(() => {
      const results = props.bulkStatus?.results ?? []
      const resultByAggregationId = new Map(
        results.map(result => [Number(result.aggregationId), result.state]),
      )

      const runIds = props.bulkStatus?.aggregationIds ?? []
      const targetData = runIds.length > 0
        ? props.aggregationsData.filter(data => runIds.map(Number).includes(Number(data.attributes.id)))
        : props.aggregationsData

      return targetData.map(data => {
        const { id, name } = data.attributes
        const state = resultByAggregationId.get(Number(id)) || 'running'

        return { id, name, state }
      })
    })

    // 「完了する」の実行対象（未完了）の件数。saved（完了済み）/ progress（集計中）以外。
    const pendingCount = computed(() => (
      props.aggregationsData.reduce((acc, data) => {
        const s = props.statusResolver(data.attributes.id)

        return (s !== 'saved' && s !== 'progress') ? acc + 1 : acc
      }, 0)
    ))

    // 全集計表が完了済み（実行対象 0 件）か。「完了する」無効化の理由表示に使う。
    const allSaved = computed(() => (
      props.aggregationsData.length > 0 && pendingCount.value === 0
    ))

    // 「完了する」可否。未完了の集計表があり、集計中でも完了処理中でもないこと。
    const canComplete = computed(() => (
      pendingCount.value > 0
      && !isAnyJobInProgress.value
      && !isBulkCompleting.value
    ))

    const onComplete = () => {
      if (!canComplete.value) return
      // 親（index.vue）でこのカテゴリ type の未完了分の bulk_complete を起動する。
      emit('bulk-complete', props.type)
    }

    // ⋮「完了済みも含めて全て集計」。完了済みも reset→集計→保存し直す（force-all）。
    // 押下では即実行せず、まず警告モーダルを開く。
    const forceAllDialogOpen = ref(false)
    const onCompleteAll = () => {
      if (isBulkCompleting.value || isAnyJobInProgress.value) return
      forceAllDialogOpen.value = true
    }
    const confirmCompleteAll = () => {
      forceAllDialogOpen.value = false
      emit('bulk-complete-all', props.type)
    }

    // --- 集計表単位の個別集計（リセット→集計→保存）----------------------
    // アクティブシートが個別集計の実行中か。
    const isActiveIndividualCompleting = computed(() => (
      !!props.individualCompletingIds[activeSheet.value.id]
    ))

    const onIndividualComplete = () => {
      const { id } = activeSheet.value
      if (!id) return
      if (isActiveIndividualCompleting.value || isBulkCompleting.value) return
      // 親（index.vue）でこの集計表だけの bulk_complete を起動する。
      emit('individual-complete', { aggregationId: id, type: props.type })
    }

    // --- クリップボードにコピー -----------------------------------------
    const gridRef = ref(null)

    const copyToClipboard = async () => {
      // AggregationGrid が組み立てた TSV（ヘッダー＋データ行＋合計行）を取得する
      const text = gridRef.value?.buildClipboardText?.()
      if (!text) {
        vm.$toast.error('コピーできるデータがありません')

        return
      }

      try {
        await navigator.clipboard.writeText(text)
        vm.$toast.info('クリップボードにコピーしました')
      } catch (e) {
        vm.$toast.error('クリップボードへのコピーに失敗しました')
      }
    }

    // --- ユーザー別 販売実績パネル ---------------------------------------
    // 旧 eod-closing/Aggregations.vue の openUserPanel / fetchPerformanceForUser
    // 相当のロジックを移植。ユーザー集計でユーザー名クリック時にドロワーを開く。
    const {
      isLoading: isPerformanceLoading,
      fetch: fetchPerformanceSummaries,
      summaryForUser,
      clearCache: clearPerformanceCache,
    } = useUserPerformanceSummaries()

    const selectedUser = ref(null)
    const isPanelOpen = ref(false)

    const selectedUserSummary = computed(() => {
      if (!selectedUser.value) {
        return {
          items: [], totalCount: 0, totalPrice: 0, workingMinutes: 0,
        }
      }

      return summaryForUser(selectedUser.value.userId)
    })

    // ユーザー名クリック時の都度フェッチ。未取得なら API、取得済みなら即時 return。
    const openUserPanel = payload => {
      selectedUser.value = payload
      isPanelOpen.value = true
      if (!props.date || !payload?.userId) return
      fetchPerformanceSummaries(props.date, payload.userId).catch(() => {
        vm.$toast.error('販売実績の取得に失敗しました')
      })
    }

    // 営業日が変わったらキャッシュをクリア（次回クリック時に最新を取得）。
    watch(() => props.date, () => {
      clearPerformanceCache()
    })

    return {
      activeIndex,
      activeSheet,
      activeStatus,
      isGridBusy,
      activeAggregation,
      activeErrorMessage,
      activeTimestamp,
      unsavedCount,
      needsReaggregationCount,
      statusIcon,
      statusColor,
      isUserAggregation,
      usersDialogOpen,
      activeAggregationUsers,
      activeAvailableUsers,
      onFetchAvailableUsers,
      onCreateUser,
      onDestroyUser,
      isBulkCompleting,
      bulkTotalCount,
      bulkDoneCount,
      progressItems,
      pendingCount,
      allSaved,
      canComplete,
      onComplete,
      isAnyJobInProgress,
      onCompleteAll,
      forceAllDialogOpen,
      confirmCompleteAll,
      isActiveIndividualCompleting,
      onIndividualComplete,
      gridRef,
      copyToClipboard,
      isPerformanceLoading,
      selectedUser,
      isPanelOpen,
      selectedUserSummary,
      openUserPanel,
      icons: {
        mdiCheckBold,
        mdiDotsVertical,
        mdiAlert,
        mdiSyncAlert,
        mdiCheck,
        mdiAlertCircle,
        mdiSkipNext,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
// カテゴリ見出し。一括実行ボタンと縦位置を揃えるため余白をリセットする。
.category-heading {
  margin: 0;
}

.sheet-bar {
  border-bottom: 1px solid rgba(0, 0, 0, .1);
}

// ⋮「保存済みも含めて全て再集計」の項目を赤字にする（破壊的操作のため警告色）。
// 無効化中は Vuetify のグレーアウトを優先する（:not で除外しないと赤のまま見える）。
.v-list-item:not(.v-list-item--disabled) .force-recompute-title {
  color: #ff5252 !important;
}

// 個別集計ボタン。Vuetify のテーマ色に依らず、確実にグレー塗りつぶし＋白文字にする。
.individual-agg-btn.v-btn:not(.v-btn--disabled) {
  background-color: #757575 !important;
  color: #fff !important;

  .v-icon {
    color: #fff !important;
  }
}

.sheet-title {
  font-size: 20px;
}

.sheet-meta {
  font-size: 11px;
  white-space: nowrap;
}

.sheet-tabs {
  border-top: 1px solid rgba(0, 0, 0, .1);
  overflow-x: auto;

  // タブの帯全体をうっすいグレー背景にする（シート本体と区別）。
  background-color: rgba(231, 227, 252, .1);
}

.sheet-tab {
  display: flex;
  align-items: center;
  white-space: nowrap;
  // 高さ・フォントを集計表グリッドのセル（.grid-cell）に揃える。
  padding: 7px 16px;
  font-size: 13px;
  cursor: pointer;
  background: transparent;
  color: inherit;
  transition: background-color .15s ease;

  // 販売実績パネルを開くユーザー名リンク（grid-user-name-link）と同じく、
  // ホバー／選択でうっすいグレー背景にする。枠線なし。
  &:hover {
    background-color: rgba(231, 227, 252, .1);
  }

  // アクティブタブは帯の上にさらにうっすいグレーが重なって少し濃く見える。
  // 文字色はユーザー名リンクと同じ通常色のまま、太字で選択中を示す。
  &.active {
    font-weight: 700;
    background-color: rgba(231, 227, 252, .1);
  }
}

// 完了する押下後の進捗チェックリスト
.bulk-progress-card {
  .bulk-progress-icon {
    font-size: 48px;
  }

  .bulk-progress-title {
    font-size: 18px;
    font-weight: 600;
  }

  .bulk-progress-summary {
    font-size: 12px;
  }

  .bulk-progress-list {
    max-width: 360px;
    text-align: left;
  }

  .bulk-progress-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-radius: 6px;
    transition: background-color .2s ease;

    & + & {
      margin-top: 4px;
    }

    &--running {
      background-color: rgba(145, 85, 253, .08);
    }
    &--succeeded {
      color: var(--v-success-base, #56CA00);
    }
    &--failed {
      color: var(--v-error-base, #FF4C51);
    }
    &--skipped {
      color: var(--text-muted, #8A8D93);
    }
  }

  .bulk-progress-icon-cell {
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .bulk-progress-step-number {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, .08);
    color: var(--text-muted, #8A8D93);
    font-size: 11px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .bulk-progress-label {
    font-size: 14px;
  }
}
</style>
